import * as THREE from 'three';
import { CARD_WIDTH, CARD_HEIGHT, CARD_THICKNESS } from '../cardMesh';

// ── Stealth smoke ────────────────────────────────────────────────
//
// Continuous volumetric-looking smoke that hugs the card's silhouette.
// Replaces the previous billboarded-puff approach. Puffs always read
// as discrete circular sprites no matter how many you overlap, so the
// cloud broke down into visible polka-dots around the border.
//
// Technique: stacked horizontal noise planes ("planar volumetrics").
// At each altitude slice above the card we render ONE plane whose
// fragment shader generates a continuous smoke density field from:
//   1. A signed-distance field to the card's rectangle (density
//      peaks at the silhouette, fades both inside and outside).
//   2. Domain-warped FBM noise sampled in card-local coordinates
//      (gives organic, curly wisps. No circular puff signature).
//   3. Per-layer phase + threshold tweaks so the stack reads as a
//      3D cloud and not as identical pancakes.
//
// Stack composites back-to-front via standard alpha blending with
// depthWrite off. The group is oriented lay-flat with yaw matching
// the card so the silhouette tracking survives taps, site rotation,
// and player-side spins. Lay-flat is forced regardless of face-down
// flip so the smoke always rises in world +Y.

const LAYER_COUNT = 8;
// RISE_HEIGHT + the per-layer base bias below must stay under
// CARD_THICKNESS (~0.32) so the entire effect column lives within
// the vertical slice occupied by the next stacked card up. That
// way a card stacked on top of this one naturally occludes the
// whole effect via the standard depth test. No upper layers
// sticking out above the stack.
const RISE_HEIGHT = 0.25;

// Plane extents in card-local units. Has to be larger than the card
// in both axes so the outside-border falloff has room to fade off
// without the plane edge becoming visible.
const PLANE_W = CARD_WIDTH * 2.3;
const PLANE_H = CARD_HEIGHT * 1.75;

const VERT_SHADER = /* glsl */ `
  uniform float uLayerT;

  varying vec2  vLocalXY;
  varying float vHeight;

  void main() {
    vLocalXY = position.xy;
    vHeight = uLayerT;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const FRAG_SHADER = /* glsl */ `
  precision highp float;

  uniform float uTime;
  uniform vec2  uCardSize;
  uniform float uLayerDensity;
  uniform vec3  uColorCore;
  uniform vec3  uColorTint;

  varying vec2  vLocalXY;
  varying float vHeight;

  // Cheap procedural value-noise. Quintic-smoothed bilinear hash
  // interpolation. Same primitive as iq's classic shader noise, fast
  // enough to run several octaves per pixel.
  float hash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
  }
  float vnoise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(
      mix(hash(i),                hash(i + vec2(1.0, 0.0)), u.x),
      mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x),
      u.y
    );
  }
  // 4-octave FBM. Multi-scale noise eliminates the obvious cellular
  // structure of single-octave noise and reads as fractal turbulence.
  float fbm(vec2 p) {
    float sum = 0.0;
    float amp = 0.5;
    for (int i = 0; i < 4; i++) {
      sum += amp * vnoise(p);
      p = p * 2.05 + vec2(1.7, 9.2);
      amp *= 0.55;
    }
    return sum;
  }
  // Domain-warped FBM: one FBM lookup produces a vector that warps
  // the sampling position of a second FBM. The result has the curly,
  // mushrooming character of real smoke instead of FBM's spotty look.
  // This is the single biggest visual lever in this shader.
  float smokeNoise(vec2 p) {
    vec2 warp = vec2(
      fbm(p + vec2(0.0, 0.0)),
      fbm(p + vec2(5.2, 1.3))
    );
    return fbm(p + warp * 1.9);
  }

  void main() {
    vec2 p = vLocalXY;

    // ── Signed distance to the card's axis-aligned rectangle.
    // sdf < 0 inside the card silhouette, 0 at the border, > 0
    // outside. Classic iq rounded-rectangle SDF with radius = 0.
    vec2 q = abs(p) - uCardSize * 0.5;
    float sdf = length(max(q, vec2(0.0))) + min(max(q.x, q.y), 0.0);

    // ── Border envelope. Density peaks right at the border and
    // tapers both inside (so puffs creep onto the card art) and
    // outside (so smoke hangs in the table area around the card).
    // Upper layers spread further than lower ones, just like a
    // real plume that widens as it rises.
    float outsideRange = mix(2.4, 6.0, vHeight);
    float insideRange  = mix(2.8, 6.5, vHeight);
    float insideEnv  = smoothstep(-insideRange, 0.5, sdf);
    float outsideEnv = 1.0 - smoothstep(0.5, outsideRange, sdf);
    float borderEnv = insideEnv * outsideEnv;
    if (borderEnv < 0.005) discard;

    // ── Animated turbulence. The noise position scrolls "down" in V
    // over time so wisps appear to flow vertically through each
    // layer, with a slow horizontal sway. Per-layer phase offsets
    // mean adjacent layers don't reveal the same pattern. The stack
    // reads as 3D vapor, not as identical pancakes.
    float t = uTime * 0.32;
    vec2 noiseUv = p * 0.17
                 + vec2(sin(t * 0.45 + vHeight * 5.0) * 1.6, -t * 1.5)
                 + vec2(vHeight * 13.7, vHeight * 4.3);

    float n = smokeNoise(noiseUv);

    // Threshold the noise into "wisp vs gap." Upper layers use a
    // slightly higher threshold → less coverage → wispier. The
    // smoothstep range is narrow on purpose so wisps have crisp
    // internal boundaries instead of smearing into a gray fog.
    float threshold = 0.28 + vHeight * 0.12;
    float wisps = smoothstep(threshold, 0.78, n);

    // Final per-pixel density.
    float density = borderEnv * wisps * uLayerDensity;
    if (density < 0.004) discard;

    // ── Color. Drift between near-pure black and a very dark
    // violet hue keyed off the noise itself, so denser regions
    // can shade slightly cooler. Multiply by density to fake
    // a tiny bit of self-shadow at thin edges.
    vec3 col = mix(uColorCore, uColorTint, n);
    col *= mix(0.78, 1.0, density);

    gl_FragColor = vec4(col, density);
  }
`;

/**
 * Build the stealth smoke effect. The returned `points` is a
 * THREE.Group containing N horizontal layers; the field is named
 * for backwards compatibility with the GameBoard call sites.
 */
export function createStealthSmokeEffect() {
  const group = new THREE.Group();
  group.frustumCulled = false;

  // All layers share one PlaneGeometry. Cuts upload cost and lets
  // each card's effect own only the per-layer materials.
  const geometry = new THREE.PlaneGeometry(PLANE_W, PLANE_H);

  const materials = [];
  // Seed every layer's time uniform with the same random offset so
  // the stack stays internally synchronised, but two cards spawning
  // stealth on the same frame don't pulse in lockstep.
  const tSeed = Math.random() * 100;

  for (let i = 0; i < LAYER_COUNT; i++) {
    const layerT = i / Math.max(LAYER_COUNT - 1, 1);
    const layerHeight = layerT * RISE_HEIGHT;

    // Exponential density falloff. Base layer reads thick, top
    // layers fade into wisps.
    const layerDensity = Math.exp(-layerT * 2.0) * 0.55;

    const material = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: tSeed },
        uCardSize: { value: new THREE.Vector2(CARD_WIDTH, CARD_HEIGHT) },
        uLayerT: { value: layerT },
        uLayerDensity: { value: layerDensity },
        uColorCore: { value: new THREE.Color(0x040408) },
        uColorTint: { value: new THREE.Color(0x1a1326) },
      },
      vertexShader: VERT_SHADER,
      fragmentShader: FRAG_SHADER,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      blending: THREE.NormalBlending,
      // Cards are sometimes viewed from below in inspection mode and
      // double-sided rendering keeps the smoke visible regardless.
      side: THREE.DoubleSide,
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.frustumCulled = false;
    // Place each plane at its altitude along the group's local +Z.
    // The group is rotated lay-flat externally, so local +Z maps to
    // world +Y. I.e. layers stack straight up in world space.
    // CARD_THICKNESS/2 lifts the bottom layer to just above the
    // card's top face; the small +0.05 bias avoids z-fighting with
    // the card mesh. Combined with RISE_HEIGHT = 0.25, the top
    // layer sits at world Y ≈ card.y + 0.46, just below the
    // touching-stack top at card.y + 0.48. So any card stacked
    // above this one occludes the entire effect column.
    mesh.position.z = layerHeight + CARD_THICKNESS / 2 + 0.05;
    // Bottom-to-top render order so back-to-front compositing is
    // correct from typical above-the-table camera angles. The
    // transparent depth-sort tiebreaker behaviour respects this when
    // distances are very close.
    mesh.renderOrder = 12 + i;

    group.add(mesh);
    materials.push(material);
  }

  return { points: group, materials, geometry };
}

const _tmpVec3 = new THREE.Vector3();
const _tmpEuler = new THREE.Euler();
export function updateStealthSmokeEffect(effect, dt, cardMesh) {
  if (!effect || !cardMesh) return;

  for (const material of effect.materials) {
    material.uniforms.uTime.value += dt;
  }

  // Position: card's world position.
  cardMesh.getWorldPosition(_tmpVec3);
  effect.points.position.copy(_tmpVec3);

  // Orient: lay-flat (group's local +Z aligned to world +Y) with the
  // card's yaw baked in so the rectangle in card-local space lines up
  // with the visible silhouette. intentEuler.z carries the card's
  // tap rotation + player-side spin; the X component is the lay-flat
  // / face-down flip, which we deliberately ignore so the smoke
  // never inverts to stack below a face-down card.
  const intent = cardMesh.userData?.intentEuler;
  const yaw = intent ? intent.z : 0;
  _tmpEuler.set(-Math.PI / 2, 0, yaw, 'XYZ');
  effect.points.rotation.copy(_tmpEuler);
}

export function disposeStealthSmokeEffect(effect) {
  if (!effect) return;
  effect.points.parent?.remove(effect.points);
  effect.geometry?.dispose();
  for (const material of effect.materials || []) {
    material.dispose();
  }
}
